import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getLimitTagsToRender,
  reOrderOptions,
} from '../FormInputs/autocompletes/Autocomplete/utils/autocomplete.utils';

export interface UseAutocompleteProps<T> {
  multiple?: boolean;
  options?: Readonly<T[]>;
  value?: T[] | T;
  getOptionLabel: (option: T) => string;
  onChangeInputText?: (value: string) => void;
  onCloseAutocomplete?: (value: string) => void;
  onInputChangeDelayed?: (value: string) => void;
  onClear?: () => void;
}

export default function useAutocomplete<T>({
  options = [],
  value,
  multiple,
  getOptionLabel,
  onChangeInputText,
  onCloseAutocomplete,
  onInputChangeDelayed,
}: UseAutocompleteProps<T>) {
  const [inputText, setInputText] = useState<string>('');
  const [isInputOpen, setIsInputOpen] = useState(false);
  const [autocompleteWidth, setAutocompleteWidth] = useState<number | null>(null);

  const normalizedValue = Array.isArray(value) && !multiple ? value[0] : value;

  const chipContainerRef = useRef<HTMLDivElement | null>(null);

  const debouncedInputChange = _.debounce((value: string) => {
    if (onInputChangeDelayed) {
      onInputChangeDelayed(value);
    }
  }, 500);

  const handleChangeInputText = (value: string) => {
    setInputText(value);
    onChangeInputText?.(value);
  };

  const handleCloseAutocomplete = () => {
    setIsInputOpen(false);

    if (inputText.length > 0 && onCloseAutocomplete) {
      onCloseAutocomplete('');
    }
  };

  const displayedOptions = useMemo(() => {
    if (!isInputOpen && multiple) {
      const selectedItems = (value || []) as T[];
      return reOrderOptions({
        options,
        selectedItems,
      });
    }
    return options;
  }, [options, isInputOpen, multiple, value]);

  useEffect(() => {
    if (chipContainerRef.current) {
      const updateWidth = () => {
        const width = chipContainerRef.current!.getBoundingClientRect().width;
        setAutocompleteWidth(width);
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);

      return () => window.removeEventListener('resize', updateWidth);
    }
  }, []);

  const limitTags = useMemo(
    () =>
      getLimitTagsToRender({
        autocompleteWidth,
        value,
        getOptionLabel,
        isInputOpen,
      }),
    [value, getOptionLabel, autocompleteWidth, isInputOpen],
  );

  const forceLimitTagsToOne = Array.isArray(value) && value.length > 0 && limitTags === 0;

  return {
    chipContainerRef,
    debouncedInputChange,
    displayedOptions,
    forceLimitTagsToOne,
    inputText,
    isInputOpen,
    limitTags,
    normalizedValue,
    handleChangeInputText,
    setIsInputOpen,
    handleCloseAutocomplete,
  };
}
